import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PrincipalLayouts } from "../../../layouts";
import { useForm } from "../../../hooks";
import { useLocation, useNavigate } from "react-router-dom";
// import { useSelector } from "react-redux";
import { NumberInput } from "../NumberInput";
import { DateInput } from "../DateInput";
import { fetchData } from "../../helpers/getData/fetchData";
import { createOneInvestments } from "../../helpers/investments";
import faradayApi from "../../../api/faradayApi";

export const AddInvestment = () => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [companiesList, setCompaniesList] = useState("isLoading");
  const [vehiclesList, setVehicles] = useState("isLoading");
  const [partnerList, setPartnerList] = useState("isLoading");
  const [isWaiting, setIsWaiting] = useState(false);
  const [formValues, handleInputChange, reset] = useForm({
    investmentDate: "",
    whoInvests: "",
    amount: "",
    vehicle: searchParams.get("vehicle"),
    individual: "",
    participationValuation: 0,
  });

  const {
    investmentDate,
    whoInvests,
    amount,
    vehicle,
    individual,
    participationValuation,
  } = formValues;

  useEffect(() => {
    const getDate = async () => {
      const { data } = await faradayApi.get(
        `/opportunities/getDateOpportunity?opportunity=${searchParams.get(
          "opportunity"
        )}`
      );
      handleInputChange({
        target: { name: "investmentDate", value: data.data },
      });
    };
    getDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setIsWaiting(true);
    createOneInvestments(
      {
        ...formValues,
        individualId: partnerList.filter((user) => user.name === individual)[0]
          ?.id,
        opportunityId: searchParams.get("opportunity"),
      },
      t("investments.successCreateMsg"),
      t("investments.errorCreateMsg")
    );
    setIsWaiting(false);
    reset();
    navigate(-1);
  };
  const handleCancel = () => {
    reset();
    navigate(-1);
  };

  useEffect(() => {
    fetchData(setPartnerList, "/user/getListNameAndId?type=Club", t);
    fetchData(setVehicles, "/vehicle/nameList", t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    if (
      partnerList !== "isLoading" &&
      partnerList.map((partner) => partner.name).includes(individual)
    ) {
      const getCompanyList = async () => {
        if (partnerList !== "isLoading") {
          const id = partnerList.filter((user) => user.name === individual)[0]
            ?.id;
          await fetchData(
            setCompaniesList,
            `/companies/listNameQuery?name=${id}`,
            t
          );
          handleInputChange({
            target: { name: "whoInvests", value: individual },
          });
        }
      };
      getCompanyList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, partnerList, individual]);

  return (
    <PrincipalLayouts>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography color="primary" variant="h5">
          {t("investments.addInvestment")}
        </Typography>
      </Stack>
      <Divider sx={{ marginTop: 2, marginBottom: 3 }} />

      <form onSubmit={handleConfirm}>
        <Stack width="100%" gap={2}>
          {partnerList !== "isLoading" && (
            <Stack direction="column" width="100%" gap={0}>
              <Typography variant="overline" color="secondary">
                {t("investments.inverter")}
              </Typography>
              <Autocomplete
                disablePortal
                options={[...partnerList.map((partner) => partner.name), ""]}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" name="individual" />
                )}
                value={individual}
                onSelect={handleInputChange}
                fullWidth
                required
              />
            </Stack>
          )}
          {partnerList !== "isLoading" &&
            partnerList.map((partner) => partner.name).includes(individual) &&
            companiesList !== "isLoading" &&
            whoInvests !== "" && (
              <>
                <Stack direction="column" width="100%" gap={0}>
                  <Typography variant="overline" color="secondary">
                    {t("investments.whoInvests")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={[...companiesList, individual]}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="whoInvests"
                      />
                    )}
                    value={whoInvests}
                    onSelect={handleInputChange}
                    fullWidth
                    required
                  />
                </Stack>

                <Stack direction="column" width="100%" gap={0}>
                  <Typography variant="overline" color="secondary">
                    {t("investments.vehicle")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={vehiclesList}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="vehicle"
                      />
                    )}
                    value={vehicle}
                    onSelect={handleInputChange}
                    fullWidth
                    required
                  />
                </Stack>

                <Stack direction="row" width="100%" gap={2}>
                  <NumberInput
                    translation="investments"
                    name={amount}
                    title="amount"
                    handleInputChange={handleInputChange}
                    required={true}
                    min={2000}
                  />
                  <NumberInput
                    translation="investments"
                    name={participationValuation}
                    title="participationValuation"
                    handleInputChange={handleInputChange}
                    required={true}
                    min={0}
                  />
                </Stack>

                <DateInput
                  translation="investments"
                  name={investmentDate}
                  title="investmentDate"
                  handleInputChange={handleInputChange}
                  required={true}
                />
              </>
            )}
        </Stack>

        <Stack direction="row" justifyContent="end" gap={2} marginTop={3}>
          <Button
            sx={{ width: "140px" }}
            color="secondary"
            onClick={handleCancel}
            variant="outlined"
          >
            {t("myprofile.cancelButton")}
          </Button>
          <Button
            disabled={isWaiting || individual === ""}
            sx={{ width: "140px" }}
            type="submit"
            variant="contained"
          >
            {t("myprofile.confirmButton")}
          </Button>
        </Stack>
      </form>
    </PrincipalLayouts>
  );
};
